import React, { useState, useRef, useCallback } from "react";
import { IonIcon } from "@ionic/react";
import { documentOutline, close } from "ionicons/icons";
import "./DragDropFileUpload.css";

interface DragDropFileUploadProps {
    onFileSelect: (file: File) => void;
    onFileRemove?: () => void;
    acceptedTypes?: string[];
    maxSize?: number; // in MB
    disabled?: boolean;
}

const DragDropFileUpload: React.FC<DragDropFileUploadProps> = ({
    onFileSelect,
    onFileRemove,
    acceptedTypes = [".png", ".pdf", ".jpeg", ".jpg"],
    maxSize = 25,
    disabled = false,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): boolean => {
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
        const isValidType = acceptedTypes.includes(fileExtension);
        const isValidSize = file.size <= maxSize * 1024 * 1024; // Convert MB to bytes

        if (!isValidType) {
            alert(`Invalid file type. Please select a ${acceptedTypes.join(", ")} file.`);
            return false;
        }

        if (!isValidSize) {
            alert(`File size too large. Maximum size is ${maxSize}MB.`);
            return false;
        }

        return true;
    };

    const handleFileSelect = useCallback(
        (file: File) => {
            if (validateFile(file)) {
                setSelectedFile(file);
                setIsUploading(true);
                setUploadProgress(0);

                // Simulate upload progress
                const interval = setInterval(() => {
                    setUploadProgress((prev) => {
                        if (prev >= 100) {
                            clearInterval(interval);
                            setIsUploading(false);
                            onFileSelect(file);
                            return 100;
                        }
                        return prev + 10;
                    });
                }, 200);
            }
        },
        [acceptedTypes, maxSize, onFileSelect]
    );

    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            if (!disabled) {
                setIsDragOver(true);
            }
        },
        [disabled]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);

            if (disabled) return;

            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                handleFileSelect(files[0]);
            }
        },
        [disabled, handleFileSelect]
    );

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                handleFileSelect(files[0]);
            }
        },
        [handleFileSelect]
    );

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        onFileRemove?.();
    }, [onFileRemove]);

    const handleClick = useCallback(() => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [disabled]);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const getFileIcon = (fileName: string) => {
        const extension = fileName.split(".").pop()?.toLowerCase();
        switch (extension) {
            case "pdf":
                return "📄";
            case "png":
            case "jpg":
            case "jpeg":
                return "🖼️";
            default:
                return "📄";
        }
    };

    return (
        <div className="drag-drop-upload-container">
            {/* Drag and Drop Area */}
            <div
                className={`drag-drop-area ${isDragOver ? "drag-over" : ""} ${disabled ? "disabled" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}>
                <div className="drag-drop-content">
                    <IonIcon icon={documentOutline} className="upload-icon" />
                    <p className="upload-text">
                        Drag and Drop file here or <span className="choose-file-link">Choose file</span>
                    </p>
                </div>
            </div>

            {/* File Requirements */}
            <div className="file-requirements">
                <div className="requirement-item">
                    <span className="requirement-label">Supported formats:</span>
                    <span className="requirement-value">{acceptedTypes.join(", ").toUpperCase()}</span>
                </div>
                <div className="requirement-item">
                    <span className="requirement-label">Maximum size:</span>
                    <span className="requirement-value">{maxSize}MB</span>
                </div>
            </div>

            {/* File Upload Progress */}
            {selectedFile && (
                <div className="file-upload-progress">
                    <div className="progress-bar-container">
                        <div className="file-info">
                            <span className="file-icon">{getFileIcon(selectedFile.name)}</span>
                            <span className="file-name">{selectedFile.name}</span>
                            <span className="file-size">{formatFileSize(selectedFile.size)}</span>
                        </div>
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                        </div>
                        <div className="progress-info">
                            <span className="progress-percentage">{uploadProgress}%</span>
                            <button className="remove-file-btn" onClick={handleRemoveFile} type="button">
                                <IonIcon icon={close} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden File Input */}
            <input ref={fileInputRef} type="file" accept={acceptedTypes.join(",")} onChange={handleFileInputChange} style={{ display: "none" }} disabled={disabled} />
        </div>
    );
};

export default DragDropFileUpload;
